import {useCallback, useRef, useState} from 'react'

const TOOLTIP_OFFSET = 10
const ESTIMATED_TOOLTIP_WIDTH = 270
const ESTIMATED_TOOLTIP_HEIGHT = 160

export default function useChartTooltip() {
  const [tooltipData, setTooltipData] = useState(null)
  const [tooltipPosition, setTooltipPosition] = useState({x: 0, y: 0})
  const tooltipSizeRef = useRef({width: ESTIMATED_TOOLTIP_WIDTH, height: ESTIMATED_TOOLTIP_HEIGHT})

  const externalTooltipHandler = useCallback(
    (context) => {
      const {tooltip, chart} = context
      if (tooltip.opacity === 0) {
        setTooltipPosition({x: 0, y: 0})
        setTooltipData(null)
        return
      }

      const canvasRect = chart.canvas.getBoundingClientRect()
      const viewportWidth = globalThis.innerWidth
      const viewportHeight = globalThis.innerHeight
      const tw = tooltipSizeRef.current.width
      const th = tooltipSizeRef.current.height

      const event = chart._lastEvent
      let screenX, screenY
      if (event?.native) {
        // Native event coordinates are already viewport-relative
        screenX = event.native.clientX
        screenY = event.native.clientY
      } else if (event && typeof event.x === 'number') {
        // Event x/y may be canvas-relative in some Chart.js versions
        screenX = canvasRect.left + event.x
        screenY = canvasRect.top + event.y
      } else {
        // Fallback to tooltip caret position (canvas-relative)
        screenX = canvasRect.left + tooltip.caretX
        screenY = canvasRect.top + tooltip.caretY
      }

      let x = screenX + TOOLTIP_OFFSET
      if (x + tw > viewportWidth) {
        x = viewportWidth - tw
      }
      if (x < 0) x = 0

      let y
      if (screenY + TOOLTIP_OFFSET + th > viewportHeight) {
        y = screenY - th - TOOLTIP_OFFSET
        if (y < 0) y = 0
      } else {
        y = screenY + TOOLTIP_OFFSET
      }

      setTooltipPosition({x, y})

      const dataIndex = tooltip.dataPoints?.[0]?.dataIndex
      const datasetIndex = tooltip.dataPoints?.[0]?.datasetIndex
      if (dataIndex != null && datasetIndex != null) {
        const rawEntry = chart.data.datasets[datasetIndex]._raw?.[dataIndex]
        if (rawEntry) {
          setTooltipData(rawEntry)
        }
      }
    },
    [setTooltipPosition, setTooltipData]
  )

  const updateTooltipSize = useCallback((node) => {
    if (node) {
      const rect = node.getBoundingClientRect()
      if (rect.width > 0 && rect.height > 0) {
        tooltipSizeRef.current = {width: rect.width, height: rect.height}
      }
    }
  }, [])

  return {externalTooltipHandler, tooltipData, tooltipPosition, updateTooltipSize}
}
